<template>
  <div
    id="welcomeOA"
    class="mainCard"
  >
    <div class="layoutRow">
      <el-row
        id="mainCounters"
        :class="modeClass"
        type="flex"
        :gutter="5"
      >
        <el-col :span="responsiveCol.head">
          <Counter
            v-loading="loading.base"
            :value="(base && base.unregistered_leader_ct
              && base.unregistered_leader_ct.invited_count)
              ? base.unregistered_leader_ct.invited_count : 0"
            :label="'welcome.pendingUsers'"
            :tooltip="$t('oa.unregisterTooltip')"
          />
        </el-col>
        <el-col :span="responsiveCol.head">
          <Counter
            v-loading="loading.base"
            :value="base ? base.active_leader_ct : 0"
            :label="'welcome.activeUsers'"
            :tooltip="$t('oa.activeTooltip')"
          />
        </el-col>
        <el-col :span="responsiveCol.head">
          <Counter
            v-loading="loading.base"
            :value="base ? base.completed_engagement_ct : 0"
            :label="'welcome.completedUsers'"
            :tooltip="$t('oa.completedTooltip')"
          />
        </el-col>
      </el-row>

      <el-card
        v-loading="loading.progress"
        class="usersGraph clear-card"
        :class="modeClass"
      >
        <div class="title">
          {{ $t('oa.activeSession.title') }}
        </div>
        <div class="content">
          <UserPerSessions
            v-if="progress"
            :chart-data.sync="progress"
          />
        </div>
      </el-card>
    </div>

    <div class="layoutRow">
      <el-card
        v-loading="loading.engagement"
        class="clear-card"
        :header="$t('oa.engagement.title')"
        :body-style="{
          padding: '20px 40px',
        }"
      >
        <Engagement
          :engagement.sync="engagement"
        />
      </el-card>
    </div>

    <div class="layoutRow">
      <el-card
        v-loading="loading.pulse"
        class="clear-card"
        :header="$t('oa.pulse.title')"
      >
        <!-- Real chart -->
        <div
          v-if="pulseData && pulseData.length"
          class="pulseContent"
        >
          <PulseAvgScore :chart-data.sync="pulseData" />
        </div>
        <!-- Dummy chart -->
        <div
          v-else
          class="pulseContent"
        >
          <PulseAvgScore
            :chart-data.sync="dummyData.pulse"
            :empty-flag="true"
          />
        </div>
      </el-card>
    </div>

    <div class="layoutRow">
      <el-card
        v-loading="loading.cvc"
        class="clear-card"
        :header="$t('oa.cvc.title')"
      >
        <!-- Real chart -->
        <div
          v-if="cvcData.list && cvcData.list.length"
          class="cvcContent"
        >
          <CvcResult
            :cvc-result="cvcData"
          />
        </div>
        <!-- Dummy chart -->
        <div
          v-else
          class="cvcContent"
        >
          <CvcResult
            :cvc-result="dummyData.cvc"
            :empty-flag="true"
          />
        </div>
      </el-card>
    </div>

    <div class="layoutRow bottomNote">
      {{ $t('oa.cvc.note') }}
    </div>
  </div>
</template>

<script>
import api from '@/api';
import Counter from './widgets/Counter.vue';

export default {
  components: {
    Counter,
    UserPerSessions: () => import(/* webpackChunkName: "Charts" */ './widgets/charts/UserPerSessions.vue'),
    PulseAvgScore: () => import(/* webpackChunkName: "Charts" */ './widgets/charts/PulseAvgScore.vue'),
    Engagement: () => import(/* webpackChunkName: "Charts" */ './widgets/charts/Engagement.vue'),
    CvcResult: () => import(/* webpackChunkName: "Charts" */ './widgets/charts/CVCResults.vue'),
  },

  data() {
    return {
      loading: {
        progress: false,
        pulse: false,
        base: false,
        engagement: false,
        cvc: false,
      },
      dummyData: {
        pulse: [
          { pulse: 'Pulse I', score: '1.2' },
          { pulse: 'Pulse II', score: '2.4' },
          { pulse: 'Pulse III', score: '4.4' },
        ],
        cvc: {
          type: ['cvc1', 'cvc2'],
          list: [
            { question: 'Q1', cvc1: 0.7 },
            { question: 'Q2', cvc1: 1.2 },
            { question: 'Q3', cvc1: 1 },
            { question: 'Q4', cvc1: 1.6 },
            { question: 'Q5', cvc1: 1.5 },
            { question: 'Q1', cvc2: 0.8 },
            { question: 'Q2', cvc2: 1.4 },
            { question: 'Q3', cvc2: 1.3 },
            { question: 'Q4', cvc2: 1.9 },
            { question: 'Q5', cvc2: 1.7 },
          ],
        },
      },
      progress: null,
      pulse: null,
      base: null,
      engagement: {
        data_list: [
          { session_id: 'session_4', leader_er: 0, stakeholder_er: 0 },
          { session_id: 'session_5', leader_er: 0, stakeholder_er: 0 },
          { session_id: 'session_6', leader_er: 0, stakeholder_er: 0 },
          { session_id: 'session_7', leader_er: 0, stakeholder_er: 0 },
          { session_id: 'session_8', leader_er: 0, stakeholder_er: 0 },
          { session_id: 'session_9', leader_er: 0, stakeholder_er: 0 },
        ],
        leader_total_er: 0,
        stakeholder_total_er: 0,
      },
      cvc: null,
    };
  },

  computed: {
    pulseData() {
      const data = [];

      if (this.pulse) {
        if (this.pulse.pulse1) {
          data.push({
            pulse: 'Pulse I',
            score: this.pulse.pulse1,
          });
        }

        if (this.pulse.pulse2) {
          data.push({
            pulse: 'Pulse II',
            score: this.pulse.pulse2,
          });
        }

        if (this.pulse.pulse3) {
          data.push({
            pulse: 'Pulse III',
            score: this.pulse.pulse3,
          });
        }
      }

      return data;
    },

    cvcData() {
      const graph = {
        type: [],
        list: [],
      };

      if (this.cvc) {
        this.cvc.forEach((item) => {
          Object.keys(item).forEach((el) => {
            if (el.includes('cvc')) {
              const number = el.split('cvc')[1];
              if (!graph.type.includes(`cvc${number}`)) { graph.type.push(`cvc${number}`); }
              const formattedObj = { question: `Q${item.question_number}` };
              formattedObj[`cvc${number}`] = item[`cvc${number}`];
              graph.list.push(formattedObj);
            }
          });
        });
      }

      return graph;
    },

    responsiveCol() {
      const col = {};
      if (['mobile', 'tablet'].includes(this.$mq)) {
        col.head = 24;
        col.one = 24;
      } else {
        col.head = 8;
        col.one = 12;
      }
      return col;
    },

    modeClass() {
      let name = 'deskMode';
      if (['mobile', 'tablet'].includes(this.$mq)) {
        name = 'mobileMode';
      }
      return name;
    },
  },

  created() {
    this.getProgress();
    this.getPulse();
    this.getBase();
    this.getEngagement();
    this.getCvcResult();
  },

  methods: {
    async getProgress() {
      try {
        this.loading.progress = true;
        const response = await api.getOaProgress();

        if (Object.entries(response.data).length) {
          this.progress = response.data;
          // const TEST = {
          //   on_boarding: 74,
          //   session_1: 272,
          //   session_2: 223,
          //   session_3: 199,
          //   session_4: 168,
          //   session_5: 160,
          //   session_6: 147,
          //   session_7: 111,
          //   session_8: 105,
          //   session_9: 85,
          //   session_10: 76,
          // };
          // this.progress = TEST;
        }
      } catch (err) {
        console.log(err);
        if (err.msg) {
          this.$message.error(err.msg);
        }
      } finally {
        this.loading.progress = false;
      }
    },

    async getPulse() {
      try {
        this.loading.pulse = true;
        const response = await api.getOaPulse();

        if (Object.entries(response.data).length) {
          this.pulse = response.data;
          // const TEST = {
          //   pulse1: 1.9,
          //   pulse2: 2.7,
          // };
          // this.pulse = TEST;
        }
      } catch (err) {
        console.log(err);
        if (err.msg) {
          this.$message.error(err.msg);
        }
      } finally {
        this.loading.pulse = false;
      }
    },

    async getBase() {
      try {
        this.loading.base = true;

        const response = await api.getOaBase();
        if (response.data) {
          this.base = response.data;
          // const TEST = {
          //   active_leader_ct: 1620,
          //   completed_engagement_ct: 240,
          //   pending_leader_ct: 140,
          // };
          // this.base = TEST;
        }
      } catch (err) {
        console.log(err);
        if (err.msg) {
          this.$message.error(err.msg);
        }
      } finally {
        this.loading.base = false;
      }
    },

    async getEngagement() {
      try {
        this.loading.engagement = true;
        const response = await api.getOaEngagement();

        if (Object.entries(response.data).length) {
          this.engagement = response.data;
          // const TEST = {
          //   data_list: [
          //     { session_id: 'session_4', leader_er: 0.68, stakeholder_er: 0.57 },
          //     { session_id: 'session_5', leader_er: 0.74, stakeholder_er: 0.60 },
          //     { session_id: 'session_6', leader_er: 0.75, stakeholder_er: 0.89 },
          //     { session_id: 'session_7', leader_er: 0.83, stakeholder_er: 0.86 },
          //     { session_id: 'session_8', leader_er: 0.84, stakeholder_er: 0.92 },
          //     { session_id: 'session_9', leader_er: 0.91, stakeholder_er: 0.100 },
          //   ],
          //   leader_total_er: 0.79,
          //   stakeholder_total_er: 0.81,
          // };
          // this.engagement = TEST;
        }
      } catch (err) {
        console.log(err);
        if (err.msg) {
          this.$message.error(err.msg);
        }
      } finally {
        this.loading.engagement = false;
      }
    },

    async getCvcResult() {
      try {
        this.loading.cvc = true;
        const response = await api.getOaCvc();

        if (Object.entries(response.data).length) {
          this.cvc = response.data;
          // const TEST = [
          //   {
          //     question_number: '1',
          //     cvc1: 1.3,
          //     cvc2: 1.4,
          //   },
          //   {
          //     question_number: '2',
          //     cvc1: 1.3,
          //     cvc2: 1.5,
          //   },
          //   {
          //     question_number: '3',
          //     cvc1: 1.6,
          //     cvc2: 1.8,
          //   },
          //   {
          //     question_number: '4',
          //     cvc1: 1.7,
          //     cvc2: 1.8,
          //   },
          //   {
          //     question_number: '5',
          //     cvc1: 1.6,
          //     cvc2: 1.8,
          //   },
          // ];
          // this.cvc = TEST;
        }
      } catch (err) {
        console.log(err);
        if (err.msg) {
          this.$message.error(err.msg);
        }
      } finally {
        this.loading.cvc = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  #welcomeOA {
    .layoutRow {
      &.bottomNote {
        color: var(--themeFontColor);
        font-size: 16px;
      }

      margin-bottom: 15px;
      align-items: stretch;

      .el-card {
        text-align: center;
        height: 100%;
      }

      .el-row {
        .el-col {
          .el-card {
            border-radius: 0;
            height: 100%;
          }
        }

        .el-col:first-child {
          .el-card {
            border-top-left-radius: 10px;
            border-bottom-left-radius: 10px;
          }
        }

        .el-col:last-child {
          .el-card {
            border-top-right-radius: 10px;
            border-bottom-right-radius: 10px;
          }
        }
      }

      &.row2 {
        align-items: stretch;

        .el-row {
          flex-wrap: wrap;

          &.mobileMode {
            .el-col:first-child {
              margin-bottom: 27px;

              .el-card {
                border-top-right-radius: 10px;
                border-bottom-right-radius: 10px;
              }
            }

            .el-col:nth-child(2) {
              .el-card {
                border-top-left-radius: 10px;
                border-bottom-left-radius: 10px;
              }
            }
          }
        }
      }

      #mainCounters {
        flex-wrap: wrap;
        margin-bottom: 5px;

        .el-card {
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
        }

        &.mobileMode {
          margin-bottom: 0;

          .el-col {
            margin-bottom: 15px;

            .el-card {
              border-radius: 10px;
            }
          }
        }
      }

      .usersGraph {
        .title {
          text-align: center;
          word-break: break-word;
          cursor: default;
          font-weight: bold;
        }

        &.deskMode {
          border-top-left-radius: 0;
          border-top-right-radius: 0;
        }

        .content {
          min-height: 150px;
        }
      }

      #stakeholderCard {
        .el-row {
          .el-col {
            padding: 20px;

            &:first-child {
              border-right: 1px solid #ebeef5;
            }

            .title {
              margin-bottom: 15px;
              cursor: default;
            }

            .counter {
              font-size: 35px;
              font-weight: bold;
            }
          }
        }
      }

      .pulseContent {
        position: relative;
        min-height: 200px;
      }

      .cvcContent {
        position: relative;
        min-height: 200px;
      }
    }
  }
</style>

<style lang="scss">
  #welcomeOA {
    .layoutRow {
      .el-card__header {
        cursor: default;
        font-weight: bold;
      }

      &.row2 {
        .el-row {
          &.deskMode {
            .el-col:first-child {
              .el-card {
                .el-card__body {
                  padding-top: 80px;
                }
              }
            }
          }
        }
      }
    }
  }
</style>
